import { useReducer, useState, useEffect } from 'react';
import { fetchTemplates, fetchTemplate, downloadCSV } from '../services/checklistService';
import {
  calcVendorTotal,
  calcMaxPossible,
  buildCSVRows,
  serializeCSV,
  getRating,
} from '../utils/checklistScoring';

// ── Feature selection reducer ────────────────────────────────────────────────

const featureInitial = { selectedIds: new Set(), weights: {}, expanded: new Set() };

const featureReducer = (state, action) => {
  switch (action.type) {
    case 'LOAD': {
      return { selectedIds: new Set(), weights: action.weights, expanded: new Set() };
    }
    case 'TOGGLE': {
      const next = new Set(state.selectedIds);
      next.has(action.id) ? next.delete(action.id) : next.add(action.id);
      return { ...state, selectedIds: next };
    }
    case 'SET_WEIGHT':
      return { ...state, weights: { ...state.weights, [action.id]: action.weight } };
    case 'TOGGLE_EXPAND': {
      const next = new Set(state.expanded);
      next.has(action.id) ? next.delete(action.id) : next.add(action.id);
      return { ...state, expanded: next };
    }
    case 'RESET':
      return { ...featureInitial, selectedIds: new Set(), expanded: new Set() };
    default:
      return state;
  }
};

// ── Rating reducer ───────────────────────────────────────────────────────────

const ratingInitial = { idx: 0, scores: {}, comments: {} };

const ratingReducer = (state, action) => {
  switch (action.type) {
    case 'SET_SCORE': {
      const cur = [...(state.scores[action.featureId] || [0, 0, 0])];
      cur[action.vendorIdx] = action.value;
      return { ...state, scores: { ...state.scores, [action.featureId]: cur } };
    }
    case 'SET_COMMENT':
      return { ...state, comments: { ...state.comments, [action.featureId]: action.text } };
    case 'SET_IDX':
      return { ...state, idx: action.idx };
    case 'RESET':
      return ratingInitial;
    default:
      return state;
  }
};

// ── Default vendors ──────────────────────────────────────────────────────────

const BLANK_VENDOR = { name: '', brand: '', website: '', product_name: '', product_url: '', is_sponsor: false };
const DEFAULT_VENDORS = [
  { ...BLANK_VENDOR, is_sponsor: true },
  { ...BLANK_VENDOR },
  { ...BLANK_VENDOR },
];

// ── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Central state and behaviour for the DIY Comparison Checklist wizard.
 * @returns {Object} Wizard API consumed by ChecklistWizard and its sub-components
 */
const useChecklistWizard = () => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(null);
  const [featureState, featureDispatch] = useReducer(featureReducer, featureInitial);
  const [vendors, setVendors] = useState(DEFAULT_VENDORS);
  const [ratingState, ratingDispatch] = useReducer(ratingReducer, ratingInitial);

  useEffect(() => {
    (async () => {
      const result = await fetchTemplates();
      if (result.success) setTemplates(result.data);
      else setError(result.error.message);
      setLoading(false);
    })();
  }, []);

  const selectTemplate = async (id) => {
    setLoading(true);
    setError(null);
    const result = await fetchTemplate(id);
    setLoading(false);
    if (!result.success) { setError(result.error.message); return; }
    const t = result.data;
    setTemplate(t);
    featureDispatch({ type: 'LOAD', weights: Object.fromEntries(t.features.map(f => [f.id, f.default_weight])) });
    ratingDispatch({ type: 'RESET' });
    setVendors([
      { name: t.meta.vendor.name, brand: t.meta.vendor.brand, website: t.meta.vendor.website, product_name: t.meta.vendor.product_name, product_url: t.meta.vendor.product_url, is_sponsor: true },
      { ...BLANK_VENDOR },
      { ...BLANK_VENDOR },
    ]);
    setStep(2);
  };

  const toggleFeature = (id) => featureDispatch({ type: 'TOGGLE', id });
  const setWeight    = (id, weight) => featureDispatch({ type: 'SET_WEIGHT', id, weight });
  const toggleExpand = (id) => featureDispatch({ type: 'TOGGLE_EXPAND', id });

  const updateVendor = (idx, field, value) =>
    setVendors(prev => { const n = [...prev]; n[idx] = { ...n[idx], [field]: value }; return n; });

  const setScore   = (featureId, vendorIdx, value) => ratingDispatch({ type: 'SET_SCORE', featureId, vendorIdx, value });
  const setComment = (featureId, text) => ratingDispatch({ type: 'SET_COMMENT', featureId, text });

  const selectedFeatures = template?.features.filter(f => featureState.selectedIds.has(f.id)) ?? [];

  const scores = vendors.map((_, i) =>
    calcVendorTotal(
      selectedFeatures,
      featureState.weights,
      Object.fromEntries(selectedFeatures.map(f => [f.id, getRating(ratingState.scores, f.id, i)]))
    )
  );

  const exportCSV = () => {
    const rows = buildCSVRows(template, selectedFeatures, featureState.weights, vendors, ratingState.scores, ratingState.comments);
    downloadCSV(serializeCSV(rows), `checklist-${template.meta.id}-${Date.now()}.csv`);
  };

  const reset = () => {
    setStep(1); setTemplate(null); setError(null);
    featureDispatch({ type: 'RESET' });
    ratingDispatch({ type: 'RESET' });
    setVendors(DEFAULT_VENDORS);
  };

  return {
    step, setStep, loading, error,
    templates, template,
    featureState, toggleFeature, setWeight, toggleExpand,
    vendors, updateVendor,
    ratingState, setScore, setComment,
    setRatingIdx: (idx) => ratingDispatch({ type: 'SET_IDX', idx }),
    selectedFeatures,
    canProceedFromFeatures: featureState.selectedIds.size >= 3 && featureState.selectedIds.size <= 5,
    canProceedFromCompetitors: vendors[1].name.trim() !== '' && vendors[2].name.trim() !== '',
    scores,
    maxPossible: calcMaxPossible(selectedFeatures, featureState.weights),
    selectTemplate,
    startRating: () => { ratingDispatch({ type: 'SET_IDX', idx: 0 }); setStep(4); },
    exportCSV,
    reset,
  };
};

export default useChecklistWizard;
